import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { BadgeColor, CellType, ColumnDef } from "../types";
import { EditColumnForm } from "./EditColumnForm";
import { FilterPopover } from "./FilterPopover";

const POPOVER_WIDTH = 256;

interface Props {
  column: ColumnDef;
  editMode: boolean;
  isFirst: boolean;
  isLast: boolean;
  // Pixel offset for columns pinned during horizontal scroll; undefined = not pinned.
  stickyLeft?: number;
  // Per-column controls available only in edit mode.
  onRename: (label: string) => void;
  onRemove: () => void;
  onMoveLeft: () => void;
  onMoveRight: () => void;
  // Type/options edit (badge & status columns get the affordance).
  onChangeType: (next: {
    type: CellType;
    options?: string[];
    optionColors?: Record<string, BadgeColor>;
  }) => void;
  // Filtering (only meaningful for status/badge columns, but the parent
  // decides whether to render the filter button based on type).
  values: Array<{ value: string; count: number }>;
  filterSelected: Set<string> | null;
  onFilterChange: (next: Set<string> | null) => void;
}

// Header cells stay pinned to the top of the table's scroll pane. They need an
// opaque background of their own — the row's background doesn't paint over
// content scrolling underneath a sticky cell.
const HEADER_CLASSES =
  "sticky top-0 z-20 px-2 py-3 text-left text-xs text-slate-500 uppercase tracking-wide font-semibold whitespace-nowrap";

export function ColumnHeader({
  column,
  editMode,
  isFirst,
  isLast,
  stickyLeft,
  onRename,
  onRemove,
  onMoveLeft,
  onMoveRight,
  onChangeType,
  values,
  filterSelected,
  onFilterChange,
}: Props) {
  const [draftLabel, setDraftLabel] = useState<string | null>(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [popoverPos, setPopoverPos] = useState<{ top: number; left: number } | null>(
    null,
  );

  // The popover is portaled to the body, so it needs viewport coordinates that
  // track the button through scrolling and resizing.
  useLayoutEffect(() => {
    if (!showFilter) return;
    const reposition = () => {
      const r = filterButtonRef.current?.getBoundingClientRect();
      if (!r) return;
      setPopoverPos({
        top: r.bottom + 4,
        left: Math.max(8, Math.min(r.left, window.innerWidth - POPOVER_WIDTH - 8)),
      });
    };
    reposition();
    // Capture phase so scrolling inside the table's pane counts too.
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [showFilter]);

  // Clicking anywhere outside dismisses the popover, matching how the other
  // overlays in the table behave.
  useEffect(() => {
    if (!showFilter) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (filterButtonRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      setShowFilter(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [showFilter]);

  function commitRename() {
    if (draftLabel === null) return;
    const trimmed = draftLabel.trim();
    if (trimmed && trimmed !== column.label) {
      onRename(trimmed);
    }
    setDraftLabel(null);
  }

  // A pinned header cell sits above its neighbours in both axes, so it needs a
  // higher stacking order than the merely top-pinned ones.
  const isPinned = stickyLeft !== undefined;
  const pinClasses = isPinned ? "z-30 border-r border-slate-200" : "";
  const pinStyle = isPinned ? { left: stickyLeft } : undefined;

  const isFilterActive = filterSelected !== null;
  // Free-text columns (where every cell is typically unique) don't benefit
  // from a categorical filter. Status / badge / number / date can repeat
  // values across rows so a checkbox picker is useful there.
  const isFilterable = column.type !== "text" && column.type !== "longText";

  // -- Display (non-edit) mode -------------------------------------------
  if (!editMode) {
    return (
      <th
        className={`${HEADER_CLASSES} ${pinClasses} bg-slate-50 relative`}
        style={pinStyle}
      >
        <div className="flex items-center gap-1">
          <span>{column.label}</span>
          {isFilterable && (
            <button
              ref={filterButtonRef}
              onClick={() => setShowFilter((v) => !v)}
              className={`ml-1 text-[10px] ${
                isFilterActive
                  ? "text-blue-600"
                  : "text-slate-300 hover:text-slate-600"
              }`}
              title={isFilterActive ? "Filter active \u2014 click to change" : "Filter rows"}
              aria-label="Filter"
            >
              {/* small funnel glyph */}
              {"\u25BC"}
            </button>
          )}
        </div>
        {showFilter &&
          isFilterable &&
          popoverPos &&
          createPortal(
            <div ref={popoverRef}>
              <FilterPopover
                values={values}
                selected={filterSelected}
                onChange={onFilterChange}
                onClose={() => setShowFilter(false)}
                style={{ top: popoverPos.top, left: popoverPos.left }}
              />
            </div>,
            document.body,
          )}
      </th>
    );
  }

  // -- Edit-columns mode -------------------------------------------------
  const canEditTypeOrOptions = column.type === "badge" || column.type === "status";
  return (
    <th
      className={`${HEADER_CLASSES} ${pinClasses} bg-blue-50 ring-1 ring-blue-100 relative`}
      style={pinStyle}
    >
      <div className="flex items-center gap-1">
        <div className="flex gap-0.5 mr-1">
          <button
            onClick={onMoveLeft}
            disabled={isFirst}
            className="p-0.5 text-slate-400 hover:text-slate-700 disabled:opacity-20 text-[10px]"
            title="Move column left"
          >
            &#9664;
          </button>
          <button
            onClick={onMoveRight}
            disabled={isLast}
            className="p-0.5 text-slate-400 hover:text-slate-700 disabled:opacity-20 text-[10px]"
            title="Move column right"
          >
            &#9654;
          </button>
        </div>
        {draftLabel === null ? (
          <button
            onClick={() => setDraftLabel(column.label)}
            className="flex-1 text-left text-slate-700 hover:text-blue-700 hover:underline"
            title="Click to rename"
          >
            {column.label}
          </button>
        ) : (
          <input
            autoFocus
            value={draftLabel}
            onChange={(e) => setDraftLabel(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitRename();
              if (e.key === "Escape") setDraftLabel(null);
            }}
            className="flex-1 px-1 py-0.5 text-xs uppercase font-semibold rounded border border-blue-300 focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
          />
        )}
        <button
          onClick={() => setShowEditForm((v) => !v)}
          className="p-0.5 text-slate-400 hover:text-blue-700 text-xs"
          title={canEditTypeOrOptions ? "Edit type or options" : "Edit type"}
          aria-label="Edit column type/options"
        >
          {/* gear-ish glyph */}
          &#9881;
        </button>
        <button
          onClick={onRemove}
          className="p-0.5 text-red-400 hover:text-red-600 text-sm"
          title="Remove this column"
        >
          &times;
        </button>
      </div>
      {showEditForm && (
        <div
          className="fixed inset-0 z-40 flex items-start justify-center bg-slate-900/30 pt-32"
          onClick={() => setShowEditForm(false)}
        >
          <div onClick={(e) => e.stopPropagation()}>
            <EditColumnForm
              column={column}
              onSave={(next) => {
                onChangeType(next);
                setShowEditForm(false);
              }}
              onCancel={() => setShowEditForm(false)}
            />
          </div>
        </div>
      )}
    </th>
  );
}
